import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AnalysisData, AnalysisCategory, AnalysisFolder, AnalysisChat, ChatMessage } from '@/types/analysis';
import {
  loadAnalysis,
  saveAnalysis,
  createChatInFolder as createChatInFolderStorage,
  updateChatMessages as updateChatMessagesStorage,
  updateChatTitle as updateChatTitleStorage,
  moveChatToFolder as moveChatToFolderStorage,
  createFolder as createFolderStorage,
  createCategory as createCategoryStorage,
  deleteChat as deleteChatStorage,
  deleteFolder as deleteFolderStorage,
  deleteAllChats as deleteAllChatsStorage,
  deleteChatsWithoutLabel as deleteChatsWithoutLabelStorage,
} from '@/services/analysisStorage';

interface AnalysisContextValue {
  data: AnalysisData;
  getChatsInFolder: (folderId: string) => AnalysisChat[];
  getFolderById: (id: string) => AnalysisFolder | undefined;
  getCategoryById: (id: string) => AnalysisCategory | undefined;
  getChatById: (id: string) => AnalysisChat | undefined;
  getFoldersByCategoryId: (categoryId: string) => AnalysisFolder[];
  addChat: (folderId: string, messages: ChatMessage[], title?: string, supplyContext?: AnalysisChat['supplyContext']) => AnalysisChat;
  updateChat: (chatId: string, messages: ChatMessage[]) => void;
  updateChatTitle: (chatId: string, title: string) => void;
  moveChat: (chatId: string, folderId: string) => void;
  createFolder: (categoryId: string, name: string) => AnalysisFolder;
  createCategory: (name: string) => AnalysisCategory;
  /** Crea carpeta (y opcionalmente categoría) y añade el chat en un solo persist para que la carpeta no se pierda en el sidebar */
  saveChatToFolderOrNew: (params: {
    categoryId: string;
    folderId?: string;
    newCategoryName?: string;
    newFolderName?: string;
    messages: ChatMessage[];
    title?: string;
  }) => { folderId: string; chatId: string };
  deleteChat: (chatId: string) => void;
  deleteFolder: (folderId: string) => void;
  deleteChatsWithoutLabel: () => void;
  deleteAllChats: () => void;
  refresh: () => void;
}

const AnalysisContext = createContext<AnalysisContextValue | null>(null);

export function AnalysisProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<AnalysisData>(() => loadAnalysis());

  const persist = useCallback((next: AnalysisData) => {
    setData(next);
    saveAnalysis(next);
  }, []);

  useEffect(() => {
    persist(loadAnalysis());
  }, []);

  const getChatsInFolder = useCallback(
    (folderId: string) => data.chats.filter((c) => c.folderId === folderId).sort((a, b) => (b.date > a.date ? 1 : -1)),
    [data.chats]
  );

  const getFolderById = useCallback((id: string) => data.folders.find((f) => f.id === id), [data.folders]);
  const getCategoryById = useCallback((id: string) => data.categories.find((c) => c.id === id), [data.categories]);
  const getChatById = useCallback((id: string) => data.chats.find((c) => c.id === id), [data.chats]);
  const getFoldersByCategoryId = useCallback(
    (categoryId: string) => data.folders.filter((f) => f.categoryId === categoryId),
    [data.folders]
  );

  const addChat = useCallback(
    (folderId: string, messages: ChatMessage[], title?: string, supplyContext?: AnalysisChat['supplyContext']) => {
      const { data: next, chat } = createChatInFolderStorage(data, folderId, messages, title, supplyContext);
      persist(next);
      return chat;
    },
    [data, persist]
  );

  const updateChat = useCallback(
    (chatId: string, messages: ChatMessage[]) => {
      persist(updateChatMessagesStorage(data, chatId, messages));
    },
    [data, persist]
  );

  const updateChatTitle = useCallback(
    (chatId: string, title: string) => {
      persist(updateChatTitleStorage(data, chatId, title));
    },
    [data, persist]
  );

  const moveChat = useCallback(
    (chatId: string, folderId: string) => {
      persist(moveChatToFolderStorage(data, chatId, folderId));
    },
    [data, persist]
  );

  const createFolder = useCallback(
    (categoryId: string, name: string) => {
      const { data: next, folder } = createFolderStorage(data, categoryId, name);
      persist(next);
      return folder;
    },
    [data, persist]
  );

  const createCategory = useCallback(
    (name: string) => {
      const { data: next, category } = createCategoryStorage(data, name);
      persist(next);
      return category;
    },
    [data, persist]
  );

  const saveChatToFolderOrNew = useCallback(
    (params: {
      categoryId: string;
      folderId?: string;
      newCategoryName?: string;
      newFolderName?: string;
      messages: ChatMessage[];
      title?: string;
    }) => {
      let next = data;
      let targetCategoryId = params.categoryId;
      let targetFolderId = params.folderId;

      if (params.newCategoryName?.trim()) {
        const { data: nextCat, category } = createCategoryStorage(next, params.newCategoryName.trim());
        next = nextCat;
        targetCategoryId = category.id;
      }
      if (params.newFolderName?.trim()) {
        const { data: nextF, folder } = createFolderStorage(next, targetCategoryId, params.newFolderName.trim());
        next = nextF;
        targetFolderId = folder.id;
      }
      if (!targetFolderId || params.messages.length === 0) {
        throw new Error('saveChatToFolderOrNew: folderId o mensajes vacíos');
      }
      const { data: nextChat, chat } = createChatInFolderStorage(next, targetFolderId, params.messages, params.title?.trim());
      persist(nextChat);
      return { folderId: targetFolderId, chatId: chat.id };
    },
    [data, persist]
  );

  const deleteChat = useCallback(
    (chatId: string) => {
      persist(deleteChatStorage(data, chatId));
    },
    [data, persist]
  );

  const deleteFolder = useCallback(
    (folderId: string) => {
      persist(deleteFolderStorage(data, folderId));
    },
    [data, persist]
  );

  const deleteChatsWithoutLabel = useCallback(() => {
    persist(deleteChatsWithoutLabelStorage(data));
  }, [data, persist]);

  const deleteAllChats = useCallback(() => {
    persist(deleteAllChatsStorage(data));
  }, [data, persist]);

  const refresh = useCallback(() => {
    setData(loadAnalysis());
  }, []);

  const value: AnalysisContextValue = {
    data,
    getChatsInFolder,
    getFolderById,
    getCategoryById,
    getChatById,
    getFoldersByCategoryId,
    addChat,
    updateChat,
    updateChatTitle,
    moveChat,
    createFolder,
    createCategory,
    saveChatToFolderOrNew,
    deleteChat,
    deleteFolder,
    deleteChatsWithoutLabel,
    deleteAllChats,
    refresh,
  };

  return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
}

export function useAnalysis() {
  const ctx = useContext(AnalysisContext);
  if (!ctx) throw new Error('useAnalysis must be used within AnalysisProvider');
  return ctx;
}
